import { appendIf } from '@btime/helpers'
import { and, find, head, isEmpty, or } from 'ramda'
import { parse } from '@datasert/cronjs-parser'
import i18n from 'i18next'

import { getFirstPlaceLevelKey } from '../../../../utils/helpers/place'
import { hasValue } from '../../../../utils/helpers/value'
import { isActionAllowedForEntity, UPDATE_CHILD_ACTION } from '../_helpers/allowedActions'

const hasValidBulkSigners = array =>
  isEmpty(array) ||
  find(item => or(!Object.keys(item).includes('act'), !Object.keys(item).includes('email')))(array ?? [])

const validatePayload = form => {
  const t = i18n.getFixedT(null, ['triggers', 'validations'])
  const { action, payload } = form
  let errors = []
  switch (action) {
    case 'email':
      errors = [
        ...appendIf(!payload?.to, { path: ['payload', 'to'], message: t('validations:required') }),
        ...appendIf(!payload?.subject, { path: ['payload', 'subject'], message: t('validations:required') }),
        ...appendIf(!payload?.content, { path: ['payload', 'content'], message: t('validations:required') })
      ]
      break

    case 'webhook':
      errors = [...appendIf(!payload?.url, { path: ['payload', 'url'], message: t('validations:required') })]
      break

    case 'insert_service_order':
      errors = [
        ...appendIf(!payload?.checklist?.id, { path: ['payload', 'checklist'], message: t('validations:required') }),
        ...appendIf(!payload?.place?.id, { path: ['payload', 'place'], message: t('validations:required') })
      ]
      break

    case 'complete_service_orders':
      errors = [
        ...appendIf(!payload?.checklists?.length, {
          path: ['payload', 'checklists'],
          message: t('validations:required')
        }),
        ...appendIf(!payload?.days, { path: ['payload', 'days'], message: t('validations:required') })
      ]
      break
    case 'complete_non_compliances':
      errors = [
        ...appendIf(!payload?.checklists?.length, {
          path: ['payload', 'checklists'],
          message: t('validations:required')
        }),
        ...appendIf(!payload?.days, { path: ['payload', 'days'], message: t('validations:required') })
      ]
      break

    case 'schedule_insert_service_order':
      errors = [
        ...appendIf(!payload?.checklist?.id, { path: ['payload', 'checklist'], message: t('validations:required') }),
        ...appendIf(!payload?.place?.id, { path: ['payload', 'place'], message: t('validations:required') })
      ]
      break

    case 'complete_service_orders':
      errors = [
        ...appendIf(!payload?.checklists?.length, { path: ['payload', 'checklists'], message: 'Campo obrigatório' }),
        ...appendIf(!payload?.days, { path: ['payload', 'days'], message: 'Campo obrigatório' })
      ]
      break

    case 'complete_non_compliances':
      errors = [
        ...appendIf(!payload?.checklists?.length, { path: ['payload', 'checklists'], message: 'Campo obrigatório' }),
        ...appendIf(!payload?.days, { path: ['payload', 'days'], message: 'Campo obrigatório' })
      ]
      break

    case 'schedule_insert_service_order':
      errors = [
        ...appendIf(!payload?.checklist?.id, { path: ['payload', 'checklist'], message: 'Campo obrigatório' }),
        ...appendIf(!payload?.place?.id, { path: ['payload', 'place'], message: 'Campo obrigatório' })
      ]
      break

    case 'insert_service_orders':
      if (!payload || !payload.length) {
        errors = [...appendIf(!payload?.checklist?.id, { path: ['payload'], message: t('triggers:minimumOneRecord') })]
      } else {
        errors = payload.reduce(
          (errors, item, index) => [
            ...errors,
            ...appendIf(!item.checklist?.id, {
              path: ['payload', index, 'checklist'],
              message: t('validations:required')
            }),
            ...appendIf(!item.place?.id, { path: ['payload', index, 'place'], message: t('validations:required') })
          ],
          []
        )
      }
      break

    case 'schedule_insert_service_orders':
      if (!payload || !payload.length) {
        errors = [...appendIf(!payload?.checklist?.id, { path: ['payload'], message: 'Mínimo de 1 registro' })]
      } else {
        errors = payload.reduce(
          (errors, item, index) => [
            ...errors,
            ...appendIf(!item.checklist?.id, { path: ['payload', index, 'checklist'], message: 'Campo obrigatório' }),
            ...appendIf(!item.place?.id, { path: ['payload', index, 'place'], message: 'Campo obrigatório' })
          ],
          []
        )
      }
      break

    case UPDATE_CHILD_ACTION: {
      // Mesmos campos que a sentença mostra como "dados a alterar" (exceto `checklists`, que só escolhe a filha alvo).
      const hasDataToChange =
        payload?.name ||
        payload?.description ||
        payload?.place ||
        payload?.asset ||
        payload?.scheduling ||
        (Array.isArray(payload?.user_or_group) ? payload.user_or_group.length > 0 : payload?.user_or_group) ||
        payload?.automatic ||
        payload?.tag ||
        payload?.documents?.length ||
        payload?.priority ||
        payload?.status?.id ||
        payload?.address?.search ||
        payload?.metadata ||
        payload?.field_values?.some(fv => fv?.value)

      errors = [
        ...appendIf(!hasDataToChange, {
          path: ['payload'],
          message: t('triggers:updateChildServiceOrder.nothingToChange')
        })
      ]
      break
    }

    case 'update_service_order_name':
      if (!payload?.content) {
        errors = [{ path: ['payload'], message: t('validations:required') }]
      }

      break

    case 'insert_place':
      const placeLevelKey = getFirstPlaceLevelKey(payload)

      errors = [
        ...appendIf(!payload[placeLevelKey]?.id, {
          path: ['payload', 'name'],
          message: t('triggers:selectAtLeastOneLevel')
        }),
        ...appendIf(!payload?.type, { path: ['payload', 'type'], message: t('validations:required') }),
        ...appendIf(!payload?.name, { path: ['payload', 'name'], message: t('validations:required') })
      ]
      break

    case 'slack_channel_message':
      errors = [
        ...appendIf(!payload?.channel?.id, {
          path: ['payload', 'channel'],
          message: t('validations:required')
        }),
        ...appendIf(!payload?.message, { path: ['payload', 'message'], message: t('validations:required') })
      ]
      break

    case 'slack_direct_message':
      const isFieldValueFunction = (payload?.user?.email || '').startsWith('{{#find_field_value')

      errors = [
        ...appendIf(!payload.user?.email, {
          path: ['payload', 'user', 'email'],
          message: t('validations:required')
        }),
        ...appendIf(!payload?.message, { path: ['payload', 'message'], message: t('validations:required') }),
        ...appendIf(isFieldValueFunction && !payload?.user?.field_origin_email, {
          path: ['payload', 'user', 'field_origin_email'],
          message: t('validations:required')
        })
      ]
      break

    case 'd4sign_send_to_sing':
      errors = [
        ...appendIf(hasValidBulkSigners(payload?.signers), {
          path: ['payload', 'signers'],
          message: t('validations:required')
        }),
        ...appendIf(!payload?.safe, { path: ['payload', 'safe'], message: t('validations:required') }),
        ...appendIf(!payload?.document, { path: ['payload', 'document'], message: t('validations:required') })
      ]
      break

    case 'd4sign_gen_pdf_and_send_to_sing':
      errors = [
        ...appendIf(!payload?.signers, { path: ['payload', 'signers'], message: t('validations:required') }),
        ...appendIf(!payload?.safe, { path: ['payload', 'safe'], message: t('validations:required') }),
        ...appendIf(!payload?.template?.id, { path: ['payload', 'template'], message: t('validations:required') })
      ]
      break

    case 'google_calendar_insert_event':
      errors = [
        ...appendIf(!payload?.name, { path: ['payload', 'name'], message: t('validations:required') }),
        ...appendIf(!payload?.start, { path: ['payload', 'start'], message: t('validations:required') }),
        ...appendIf(!payload?.duration, { path: ['payload', 'duration'], message: t('validations:required') })
      ]
      break

    case 'whatsapp_by_chat_pro':
      const chatProVariableKeys = Object.keys(payload?.template?.labels || {})
      errors = [
        ...appendIf(!String(payload?.number ?? '').trim(), {
          path: ['payload', 'number'],
          message: t('validations:required')
        }),
        ...appendIf(!payload?.template?.name, {
          path: ['payload', 'template'],
          message: t('validations:required')
        }),
        ...chatProVariableKeys.flatMap(key =>
          appendIf(!String(payload?.template?.variables?.[key] ?? '').trim(), {
            path: ['payload', 'template', 'variables', key],
            message: t('validations:required')
          })
        )
      ]
      break
    case 'update_place':
      const fieldValuesWithValue =
        payload?.field_values?.filter(fv => {
          return !!fv.value
        }) || []

      const remapped = fieldValuesWithValue.map(fv => {
        return {
          name: fv.field?.name || fv.name,
          field_id: fv.field_id,
          value: fv.value
        }
      })
      errors = [
        ...appendIf(!payload?.name && !remapped?.length, {
          path: ['payload', ''],
          message: t('validations:required')
        })
      ]
      break

    case 'update_service_order_user_or_group':
      if (!payload?.content) {
        errors = [{ path: ['payload'], message: t('validations:required') }]
      }
      break

    case 'update_status':
      if (!payload?.status) {
        errors = [{ path: ['payload', 'status'], message: t('validations:required') }]
      }
      break

    case 'update_parent_status':
      if (!payload?.status) {
        errors = [{ path: ['payload', 'status'], message: t('validations:required') }]
      }
      break

    default:
      break
  }

  if (errors.length > 0) {
    errors = [...errors, { path: ['payload'], message: t('triggers:invalidPayload') }]
  }

  return errors
}

const REPLY_VALUE_FIELDS = ['_event_service_order_reply_value', '_form_reply_reply_value']
const MULTIPLE_REPLY_OPERATORS = ['any', 'nany', 'exactly']

const isMultipleReplyValueCondition = condition => {
  if (!REPLY_VALUE_FIELDS.includes(condition?.field)) return false
  const questionType = condition?.data?.question?.typeName
  if (questionType === 'multiple') return true
  if (questionType === 'select') return false
  if (MULTIPLE_REPLY_OPERATORS.includes(condition?.operator)) return true
  if (typeof condition?.origin_value === 'string' && condition.origin_value.includes('to_refs')) return true
  return false
}

const validateConditions = conditions => {
  const t = i18n.getFixedT(null, 'validations')
  return conditions?.reduce((errors, condition, index) => {
    const allowNullUserEq =
      condition.value === null && (condition?.field || '').endsWith('user_id') && (condition?.operator || 'eq') === 'eq'

    if (isMultipleReplyValueCondition(condition)) {
      const value = condition.value
      const isArray = Array.isArray(value)
      const questionId = condition?.data?.question?.id || condition?.data?.question_id
      const allowEmpty = condition.operator === 'exactly'

      return [
        ...errors,
        ...appendIf(!condition.field, { path: ['conditions', index, 'field'], message: t('required') }),
        ...appendIf(!questionId, { path: ['conditions', index, 'data', 'question'], message: t('required') }),
        ...appendIf(!isArray, { path: ['conditions', index, 'value'], message: t('required') }),
        ...appendIf(isArray && !allowEmpty && value.length === 0, {
          path: ['conditions', index, 'value'],
          message: t('required')
        })
      ]
    }

    return [
      ...errors,
      ...appendIf(!condition.field, { path: ['conditions', index, 'field'], message: t('required') }),
      ...appendIf(!allowNullUserEq && !hasValue(condition.value), {
        path: ['conditions', index, 'value'],
        message: t('required')
      })
    ]
  }, [])
}

const validateFrequency = form => {
  const t = i18n.getFixedT(null, 'validations')
  if (!form.event.startsWith('every_')) return []

  const { hour, minute, day_of_week, day_of_month } = head(parse(form.frequency || '* * * * *')?.expressions) || {}

  const needsDays =
    ['every_week', 'every_month'].includes(form.event) && !day_of_week?.values?.length && !day_of_month.values?.length

  return [
    ...appendIf(!hour?.values?.length || !minute.values?.length, { path: ['time'], message: t('required') }),
    ...appendIf(needsDays, { path: ['days'], message: t('required') })
  ]
}

export const validate = (form, setErrors) => {
  const t = i18n.getFixedT(null, ['triggers', 'validations'])
  const errors = [
    ...appendIf(!!form?.readOnly, { path: ['entity'], message: t('triggers:triggerCannotBeEdited') }),
    ...appendIf(!form.entity, { path: ['entity'], message: t('validations:required') }),
    ...appendIf(!form.event, { path: ['event'], message: t('validations:required') }),
    ...appendIf(!form.action, { path: ['action'], message: t('validations:required') }),
    ...appendIf(!!form.action && !isActionAllowedForEntity(form.action, form.entity), {
      path: ['action'],
      message: t('triggers:actionNotAllowedForTrigger')
    }),
    ...validateConditions(form.conditions || []),
    ...validatePayload(form),
    ...validateFrequency(form)
  ]

  setErrors(errors)
  return errors.length === 0
}
